import React, { forwardRef, useState, useId, useEffect } from 'react';
import { cn } from '../../lib/utils';

export interface TextareaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'size'> {
  label?: string;
  error?: string | boolean;
  description?: string;
  required?: boolean;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'default' | 'filled' | 'unstyled';
  autosize?: boolean;
  minRows?: number;
  maxRows?: number;
  maxLength?: number;
  showCount?: boolean;
}

const sizeClasses = {
  sm: 'text-13',
  md: 'text-13',
  lg: 'text-sm'
};

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      label,
      error,
      description,
      required,
      size = 'md',
      variant = 'default',
      autosize = false,
      minRows = 3,
      maxRows = 10,
      maxLength,
      showCount = false,
      className = '',
      id: providedId,
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const id = providedId || generatedId;
    const [charCount, setCharCount] = useState(0);
    const errorMessage = typeof error === 'string' ? error : error ? 'This field has an error' : '';

    useEffect(() => {
      if (value) {
        setCharCount(String(value).length);
      }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setCharCount(e.target.value.length);
      onChange?.(e);
    };

    const textareaClasses = cn(
      'flex min-h-[80px] w-full rounded-md border border-border bg-transparent px-2.5 py-1.5 placeholder:text-muted-foreground-subtle transition-colors duration-200 focus-visible:outline-none focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-0 hover:border-muted-foreground/30 disabled:cursor-not-allowed disabled:opacity-50',
      sizeClasses[size],
      error && 'border-destructive focus-visible:border-destructive focus-visible:ring-destructive/40',
      variant === 'unstyled' && 'border-0',
      className
    );

    return (
      <div className="w-full">
        {label && (
          <label htmlFor={id} className="mb-2 block text-11 font-medium text-muted-foreground">
            {label}
            {required && <span className="ml-0.5 text-destructive">*</span>}
          </label>
        )}
        {description && (
          <p className="mb-2 text-sm text-muted-foreground">{description}</p>
        )}
        <div className="relative">
          <textarea
            ref={ref}
            id={id}
            rows={minRows}
            className={textareaClasses}
            onChange={handleChange}
            aria-invalid={Boolean(error)}
            aria-describedby={error ? `${id}-error` : undefined}
            maxLength={maxLength}
            value={value}
            {...props}
          />
          {showCount && maxLength && (
            <div className="mt-1 text-right text-xs text-muted-foreground">
              {charCount}/{maxLength}
            </div>
          )}
        </div>
        {errorMessage && (
          <p id={`${id}-error`} className="mt-1.5 text-sm text-destructive">
            {errorMessage}
          </p>
        )}
      </div>
    );
  }
);

Textarea.displayName = 'Textarea';